import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage, LANGUAGES } from "../context/LanguageContext";
import { useTranslation } from "../translations";
import { useHome } from "../translations/home";
import { trackCtaClick } from "../lib/ctaTracking";
import { useRefonte } from "../translations/refonte";

const LOGO_URL = "/cayus-logo.png";

// Flag images (SVG-based for better quality)
const FLAGS = {
  en: (
    <svg className="w-5 h-4 rounded-sm" viewBox="0 0 60 30">
      <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
      <path d="M0,0 v30 h60 v-30 z" fill="#00247d"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#cf142b" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#cf142b" strokeWidth="6"/>
    </svg>
  ),
  fr: (
    <svg className="w-5 h-4 rounded-sm" viewBox="0 0 30 20">
      <rect width="30" height="20" fill="#ED2939"/>
      <rect width="20" height="20" fill="#fff"/>
      <rect width="10" height="20" fill="#002395"/>
    </svg>
  ),
  es: (
    <svg className="w-5 h-4 rounded-sm" viewBox="0 0 30 20">
      <rect width="30" height="20" fill="#c60b1e"/>
      <rect y="5" width="30" height="10" fill="#ffc400"/>
    </svg>
  ),
};

const Navbar = ({ isScrolled, ctaAfterHero = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [mobileProductOpen, setMobileProductOpen] = useState(false);
  const { language, switchLanguage } = useLanguage();
  const t = useTranslation(language);
  const n = useRefonte(language).nav;
  const homeCta = useHome(language).cta;

  const productLinks = [
    { label: n.managers, href: "/produit/managers" },
    { label: n.senior, href: "/produit/senior-managers" },
    { label: n.hr, href: "/produit/rh" },
    { label: n.exco, href: "/produit/comite-executif" },
  ];

  const navLinks = [
    { label: n.methodology, href: "/methodologie" },
    { label: n.pilotProgram, href: "/programme-pilote" },
    { label: n.security, href: "/securite" },
    { label: n.why, href: "/pourquoi-cayus" },
    { label: n.blog, href: "/blog" },
  ];

  useEffect(() => {
    if (!ctaAfterHero) return;
    const onScroll = () => setPastHero(window.scrollY > 520);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ctaAfterHero]);

  const scrollToDemo = () => {
    trackCtaClick("navbar", language);
    const element = document.querySelector("#demo-form");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = "/#demo-form";
    }
    setMobileMenuOpen(false);
  };

  const languageOrder = ["en", "fr", "es"];

  return (
    <nav
      className={`navbar fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "scrolled" : ""
      }`}
      data-testid="navbar"
    >
      <div className="wrap-cayus">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center"
            data-testid="navbar-logo"
          >
            <img
              src={LOGO_URL}
              alt="Cayus AI"
              className="h-8 lg:h-10 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 ml-12">
            <div
              className="relative"
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={() => setProductOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-sm font-medium text-[#4D4D4D] hover:text-[#0047AB] transition-colors py-2"
                data-testid="nav-product-dropdown"
                aria-expanded={productOpen}
              >
                {n.product}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${productOpen ? "rotate-180" : ""}`} />
              </button>
              {productOpen && (
                <div className="absolute left-0 top-full w-64 bg-white rounded-xl shadow-lg border border-[#D9D9D9] py-2 z-50">
                  {productLinks.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      className="block px-4 py-2.5 text-sm text-[#3A3F4B] hover:bg-[#F3F8FE] hover:text-[#0047AB] transition-colors"
                      data-testid={`nav-product-${l.href.split("/").pop()}`}
                    >
                      {l.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-underline text-sm font-medium text-[#4D4D4D] hover:text-[#0047AB] transition-colors"
                data-testid={`nav-link-${link.href.replace("/", "")}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA & Language */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              onClick={scrollToDemo}
              className={`btn-cayus rounded-md px-5 bg-[#0047AB] text-white text-sm font-medium hover:bg-[#0B3F8F] ${
                ctaAfterHero && !pastHero ? "nav-cta-hidden" : "nav-cta-shown"
              }`}
              data-testid="nav-demo-cta"
            >
              {homeCta.label}
            </Button>
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#F3F3F3] transition-colors"
                data-testid="language-selector"
              >
                {FLAGS[language]}
                <ChevronDown className={`w-4 h-4 text-[#5E6C83] transition-transform ${langMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {langMenuOpen && (
                <div 
                  className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-[#D9D9D9] py-1 z-50"
                  onMouseLeave={() => setLangMenuOpen(false)}
                >
                  {languageOrder.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        switchLanguage(lang);
                        setLangMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#F7F9FC] transition-colors ${
                        language === lang ? "bg-[#F3F8FE] text-[#0047AB]" : "text-[#3A3F4B]"
                      }`}
                      data-testid={`lang-option-${lang}`}
                    >
                      {FLAGS[lang]}
                      <span>{LANGUAGES[lang].name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile: Language + Menu button */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Mobile Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1 px-2 py-2 rounded-lg hover:bg-[#F3F3F3]"
                data-testid="mobile-language-selector"
              >
                {FLAGS[language]}
              </button>

              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-[#D9D9D9] py-1 z-50">
                  {languageOrder.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        switchLanguage(lang);
                        setLangMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-[#F7F9FC] ${
                        language === lang ? "bg-[#F3F8FE] text-[#0047AB]" : "text-[#3A3F4B]"
                      }`}
                    >
                      {FLAGS[lang]}
                      <span>{LANGUAGES[lang].name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="p-2 text-[#4D4D4D]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden py-4 border-t border-[#D9D9D9] bg-white"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {/* Produit accordion */}
              <button
                onClick={() => setMobileProductOpen(!mobileProductOpen)}
                className="flex items-center justify-between text-left text-sm font-medium text-[#4D4D4D] hover:text-[#0047AB] transition-colors py-2"
                data-testid="mobile-product-toggle"
              >
                {n.product}
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileProductOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileProductOpen && (
                <div className="pl-4 flex flex-col gap-1 border-l border-[#D9D9D9]">
                  {productLinks.map((l) => (
                    <a key={l.href} href={l.href} className="text-sm text-[#4D4D4D] hover:text-[#0047AB] py-1.5">
                      {l.label}
                    </a>
                  ))}
                </div>
              )}
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-left text-sm font-medium text-[#4D4D4D] hover:text-[#0047AB] transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              <Button
                onClick={scrollToDemo}
                className="mt-2 rounded-full bg-[#0047AB] text-white font-medium hover:bg-[#0B3F8F]"
                data-testid="mobile-cta-button"
              >
                {homeCta.label}
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
